import './index.css';

import { QueryClientProvider } from '@tanstack/react-query';
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import App from './App';
import { amplifyConfig } from './config/amplify';
import { queryClient } from './config/queryClient';
import { initAnalytics, trackAuthError } from './services/analytics';
import { useAuthStore } from './stores/authStore';
import { logger } from './utils/logger';

// Configure Amplify
Amplify.configure(amplifyConfig);

// Initialize analytics
initAnalytics();

// Listen for Amplify auth events (OAuth completion, sign out, etc.)
// This ensures auth state is updated when OAuth redirect completes
Hub.listen('auth', ({ payload }) => {
  switch (payload.event) {
    case 'signInWithRedirect':
      // No-op: AuthCallback handles auth checking with retry logic.
      // Calling checkAuthStatus() here races with the callback page and
      // can set isAuthenticated=false before tokens are ready.
      logger.debug('Hub: signInWithRedirect event received');
      break;
    case 'signInWithRedirect_failure': {
      // OAuth sign-in failed - track error and clear loading state
      const error = payload.data?.error;
      const errorMessage =
        error instanceof Error ? error.message : String(error || 'Sign-in failed');
      const errorCode = error instanceof Error ? error.name : undefined;
      // Try to detect provider from URL params (Cognito includes identity_provider)
      const urlParams = new URLSearchParams(window.location.search);
      const identityProvider = urlParams.get('identity_provider')?.toLowerCase();
      const provider: 'apple' | 'google' | 'email' =
        identityProvider === 'signinwithapple'
          ? 'apple'
          : identityProvider === 'google'
            ? 'google'
            : 'email'; // Fallback when provider is missing or unrecognized
      trackAuthError(provider, 'callback', errorMessage, errorCode);
      useAuthStore.setState({
        isLoading: false,
        error: 'Sign-in failed. Please try again.',
      });
      break;
    }
    case 'signedOut':
      // User signed out - clear local state directly (don't call logout() which
      // calls amplifySignOut() again, causing an infinite loop)
      useAuthStore.setState({ user: null, isAuthenticated: false, error: null });
      break;
  }
});

// Check auth status on app initialization (resolves isLoading for all pages)
useAuthStore.getState().checkAuthStatus();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>
);
